import React from 'react';
import { 
  Box, 
  Typography, 
  Avatar, 
  AvatarGroup, 
  Tooltip, 
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  styled
} from '@mui/material';
import { useCollaboration } from './CollaborationProvider';
import { PersonOutline as PersonIcon, Edit as EditIcon } from '@mui/icons-material';

// Create styled badge for online status
const OnlineBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

interface ActiveCollaboratorsProps {
  showDetailed?: boolean;
}

const ActiveCollaborators: React.FC<ActiveCollaboratorsProps> = ({ showDetailed = false }) => {
  const { activeUsers, connected, documentLocked, lockedBy } = useCollaboration();
  
  // Create initials from username
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Get background color based on username (for consistent colors per user)
  const getAvatarColor = (name: string): string => {
    const colors = [
      '#f44336', '#e91e63', '#9c27b0', '#673ab7', 
      '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4',
      '#009688', '#4caf50', '#8bc34a', '#cddc39', 
      '#ffc107', '#ff9800', '#ff5722'
    ];
    
    // Simple hash function to get consistent color
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  // Compact view with avatar group
  if (!showDetailed) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <AvatarGroup max={5} sx={{ mr: 1 }}>
          {activeUsers.map(user => (
            <Tooltip key={user.userId} title={user.username}>
              <Avatar 
                sx={{ 
                  bgcolor: getAvatarColor(user.username),
                  width: 32,
                  height: 32,
                  fontSize: '0.875rem'
                }}
              >
                {getInitials(user.username)}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>
        
        {documentLocked && (
          <Tooltip title={`Document locked by ${lockedBy}`}>
            <EditIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
          </Tooltip>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {activeUsers.length === 0 
            ? 'No active collaborators' 
            : `${activeUsers.length} ${activeUsers.length === 1 ? 'collaborator' : 'collaborators'}`}
        </Typography>
      </Box>
    );
  }

  // Detailed view with list
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Active Collaborators
        {!connected && (
          <Typography 
            component="span" 
            color="error.main" 
            variant="caption" 
            sx={{ ml: 1 }}
          >
            (Disconnected)
          </Typography>
        )}
      </Typography>
      
      {activeUsers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No active collaborators
        </Typography>
      ) : (
        <List dense>
          {activeUsers.map(user => (
            <ListItem key={user.userId} disablePadding sx={{ mb: 1 }}>
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <OnlineBadge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  variant="dot"
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: getAvatarColor(user.username),
                      width: 32,
                      height: 32,
                      fontSize: '0.75rem'
                    }}
                  >
                    {getInitials(user.username)}
                  </Avatar>
                </OnlineBadge>
              </ListItemAvatar>
              <ListItemText 
                primary={user.username} 
                primaryTypographyProps={{ variant: 'body2' }}
              />
              
              {documentLocked && lockedBy === user.username && (
                <Tooltip title="Currently editing">
                  <EditIcon fontSize="small" color="primary" />
                </Tooltip>
              )}
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default ActiveCollaborators; 